import React, { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";

import { Auth } from "aws-amplify";
import { CognitoIdToken, CognitoRefreshToken, CognitoAccessToken, CognitoUserSession } from "amazon-cognito-identity-js";

import { AppEvent, triggerAppEvent, useAppContext } from "./app.context";
import {
  VerificationCodeRequest,
  LoginRequest,
  RegisterRequest,
  VerifyResetRequest,
  VerifyRegisterRequest,
  ChangePasswordRequest,
} from "../models/login-request.model";
import errorMessages from "../translations/en/errors.json";
import { AuthSession } from "../models/auth-session.model";
import { useLocation, useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import queryString from "query-string";
import axios from "axios";
import environment from "../env";
import { SsoExchangeResponse } from "../models/sso.model";

// Types

export interface AuthContext {
  session?: AuthSession | null;
  setSession(session: (AuthSession & { username: string }) | null): void;
  login(request: LoginRequest): Promise<void>;
  register(request: RegisterRequest): Promise<void>;
  refresh(): Promise<AuthSession>;
  verifySignUp(request: VerifyRegisterRequest): Promise<void>;
  resendSignUpCode(request: VerificationCodeRequest): Promise<void>;
  resetPassword(request: VerificationCodeRequest): Promise<void>;
  verifyResetPassword(request: VerifyResetRequest): Promise<void>;
  changePassword(request: ChangePasswordRequest): Promise<void>;
  signOut(): Promise<void>;
  isAuthReady: boolean;
}

const Context = createContext<AuthContext>(null!);

const { defaultClientId, idpClientId, userPoolId, domain: cognitoDomain } = environment.cognito;

// SSO state helpers

interface SsoStatePayload {
  csrf: string;
  providerName: string;
  email: string;
  remember?: boolean;
}

/**
 * Decodes a base64-encoded state string produced by LoginForm during app-initiated SSO.
 * Returns null if the string is missing, malformed, or not our format.
 */
function parseSsoState(state: string | undefined | null): SsoStatePayload | null {
  if (!state) return null;
  try {
    const decoded = JSON.parse(atob(state));
    if (
      typeof decoded?.csrf !== "string" ||
      typeof decoded?.providerName !== "string" ||
      typeof decoded?.email !== "string"
    ) return null;
    return decoded as SsoStatePayload;
  } catch {
    return null;
  }
}

// Session-continuity helpers
//
// Two persistence modes, chosen at login time via the "Stay logged in" checkbox:
//  - remember=false (default): the session lives only in this tab's sessionStorage, exactly
//    like the app's original behavior -- closing the tab (or opening a new one) loses it.
//  - remember=true: the backend also sets an HttpOnly refresh-token cookie (browser-instance
//    scoped, not tab-scoped) on login/refresh/SSO exchange, plus a non-HttpOnly marker cookie
//    carrying no sensitive data. That's what lets a brand new tab -- e.g. one opened from an
//    email notification link -- pick up an existing login without a re-login prompt, while
//    still being cleared the moment the browser process exits.
//
// Presence of a sessionStorage entry is used as the signal for which mode a given tab is in;
// presence of the marker cookie is used to avoid firing a silent-refresh network call when
// there's clearly no cookie-based session to restore (e.g. a fresh visit to /login).

const SESSION_STORAGE_KEY = "context/session";
const HAS_SESSION_COOKIE = "zapit_has_session";

type StoredSession = AuthSession & { username: string };

function readStoredSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeStoredSession(session: StoredSession | null): void {
  if (session) {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } else {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
}

function hasSessionCookie(): boolean {
  return document.cookie.split("; ").some((entry) => entry.startsWith(`${HAS_SESSION_COOKIE}=`));
}

const authChannel: BroadcastChannel | null = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("zapit-auth") : null;

// Provider

function AuthContextProvider(props: React.PropsWithChildren<{}>): JSX.Element {
  const history = useHistory();
  const { search } = useLocation();
  const queryClient = useQueryClient();

  const [authParams] = useState<{ code?: string; state?: string }>(queryString.parse(search) as any);

  const [isAuthReady, setIsAuthReady] = useState(false);

  // In-memory cache of the current session. Persistence (if any) happens separately via
  // writeStoredSession (sessionStorage, "remember" off) or the backend's HttpOnly cookie
  // ("remember" on) -- see the Session-continuity helpers above.
  const [session, setSession] = useState<(AuthSession & { username: string }) | null>(null);

  const sessionRef = useRef(session || null);
  const setSessionRef = useRef(setSession);
  useEffect(() => {
    setSessionRef.current = setSession;
  }, [setSession]);

  useEffect(() => {
    setIsAuthReady(false);

    // Configure Amplify (still needed for register, signOut, reset, etc.)
    const userClientType = sessionRef.current?.clientType ?? "app";
    // Any OrgSsoConfig-registered provider (Azure, Google, ...) carries its own Cognito App
    // Client ID on the session -- prefer that. Fall back to the legacy env-level idpClientId
    // only for the IdP-initiated flow below, which bypasses our BE and never gets appClientId.
    const ssoAppClientId = sessionRef.current?.appClientId ?? (userClientType === "microsoftIdp" ? idpClientId : undefined);
    Auth.configure({
      Analytics: { disabled: true },
      Auth: {
        oauth: {
          scope: ["email"],
          responseType: "code",
        },
        region: "us-east-1",
        userPoolId,
        userPoolWebClientId: ssoAppClientId || defaultClientId,
        storage: window.sessionStorage,
        authenticationFlowType: "USER_PASSWORD_AUTH",
      },
    });

    const hasAuthCode = Boolean(authParams?.code);

    if (hasAuthCode) {
      history.replace("");

      const ssoState = parseSsoState(authParams.state);

      if (ssoState) {
        // App-initiated SSO: validate CSRF then exchange code via the BE.
        const storedCsrf = sessionStorage.getItem("sso_csrf");
        sessionStorage.removeItem("sso_csrf");

        if (storedCsrf && ssoState.csrf !== storedCsrf) {
          // CSRF mismatch -- possible CSRF attack or stale tab; go back to login.
          history.replace("/login");
          setIsAuthReady(true);
          return;
        }

        axios
          .post<SsoExchangeResponse>(
            "auth/sso/exchange",
            { code: authParams.code, providerName: ssoState.providerName, email: ssoState.email, remember: ssoState.remember },
            { baseURL: environment.newApiUrl, withCredentials: true }
          )
          .then((response) => {
            const { username, accessToken, issuedAt, expiresIn, refreshToken, appClientId } = response.data;
            // clientType is "sso" for every provider -- appClientId (not clientType) is what
            // identifies the specific Cognito App Client to refresh/reconfigure Amplify against.
            const newSession: StoredSession = { username, clientType: "sso", appClientId, accessToken, issuedAt, expiresIn, refreshToken };
            writeStoredSession(ssoState.remember ? null : newSession);
            setSessionRef.current(newSession);
          })
          .catch((error) => {
            console.error("[auth] SSO exchange failed:", error);
            history.replace("/login");
          })
          .finally(() => setIsAuthReady(true));
      } else if (idpClientId) {
        // IdP-initiated SSO (Azure portal tile click): exchange code directly with Cognito.
        // Preserved for backward compatibility -- no state means this came from the IdP, not our login page.
        const urlencoded = new URLSearchParams();
        urlencoded.append("grant_type", "authorization_code");
        urlencoded.append("client_id", idpClientId);
        urlencoded.append("code", authParams.code!);
        urlencoded.append("redirect_uri", window.location.origin);

        fetch(`${cognitoDomain}/oauth2/token`, { method: "POST", body: urlencoded, redirect: "follow" })
          .then((response) => response.json())
          .then((result) => {
            if (!result.id_token || !result.access_token || !result.refresh_token) {
              return Promise.reject(new Error("Invalid token response: missing required fields"));
            }
            const localSession = new CognitoUserSession({
              IdToken: new CognitoIdToken({ IdToken: result.id_token }),
              RefreshToken: new CognitoRefreshToken({ RefreshToken: result.refresh_token }),
              AccessToken: new CognitoAccessToken({ AccessToken: result.access_token }),
            });
            // No checkbox context on this path (it bypasses our login page entirely) --
            // always tab-scoped via sessionStorage, matching this app's original behavior.
            const newSession: StoredSession = {
              username: localSession.getAccessToken().payload.username,
              clientType: "microsoftIdp",
              accessToken: localSession.getIdToken().getJwtToken(),
              issuedAt: localSession.getIdToken().getIssuedAt(),
              expiresIn: localSession.getIdToken().getExpiration(),
              refreshToken: localSession.getRefreshToken().getToken(),
            };
            writeStoredSession(newSession);
            setSessionRef.current(newSession);
          })
          .catch((error) => console.error("[auth] IdP-initiated exchange failed:", error))
          .finally(() => setIsAuthReady(true));
      } else {
        // Code present but no IdP configured and no app-initiated state -- discard.
        history.replace("/login");
        setIsAuthReady(true);
      }
    } else {
      // No auth code -- try to restore an existing session.
      const stored = readStoredSession();

      if (stored) {
        // "remember" was off at login: tab-scoped sessionStorage entry, restored instantly --
        // exactly this app's original behavior, no network round trip needed.
        setSessionRef.current(stored);
        setIsAuthReady(true);
        return;
      }

      // No sessionStorage entry -- either this is a brand new tab (possibly one opened from
      // an email link) or there's no session at all. Two independent restore paths run
      // concurrently so neither adds latency to the other:
      //  1. Our own HttpOnly refresh-token cookie ("remember" was on at login). This is
      //     browser-instance scoped (not tab-scoped), so it's what lets a brand new tab pick
      //     up an existing login without a re-login prompt.
      //  2. Amplify's own Cognito session storage, kept for backward compatibility with flows
      //     that go through Amplify directly (register/reset/confirm, IdP-initiated SSO).
      const silentRefresh = hasSessionCookie()
        ? axios
            .post<Omit<AuthSession, "clientType"> & { clientType?: string }>(
              "auth/refresh",
              {},
              { baseURL: environment.newApiUrl, withCredentials: true }
            )
            .then((response) => response.data)
            .catch(() => null)
        : Promise.resolve(null);

      const amplifyRestore = Auth.currentSession().catch(() => null);

      Promise.all([silentRefresh, amplifyRestore])
        .then(([refreshed, amplifySession]) => {
          if (refreshed) {
            setSessionRef.current({
              username: refreshed.username ?? sessionRef.current?.username ?? "",
              clientType: refreshed.clientType ?? sessionRef.current?.clientType ?? "app",
              appClientId: refreshed.appClientId ?? sessionRef.current?.appClientId,
              accessToken: refreshed.accessToken,
              issuedAt: refreshed.issuedAt,
              expiresIn: refreshed.expiresIn,
              refreshToken: refreshed.refreshToken,
            });
          } else if (amplifySession) {
            const accessToken = amplifySession.getIdToken().getJwtToken();
            const username = amplifySession.getAccessToken().payload.username;
            setSessionRef.current((prev) => {
              const clientType = sessionRef.current?.clientType ?? "app";
              if (prev?.accessToken === accessToken) return { ...prev, clientType };
              return {
                username,
                clientType,
                accessToken,
                expiresIn: amplifySession.getIdToken().getExpiration(),
                issuedAt: amplifySession.getIdToken().getIssuedAt(),
                refreshToken: amplifySession.getRefreshToken().getToken(),
              };
            });
          }
        })
        .finally(() => setIsAuthReady(true));
    }
  }, [authParams, history]);

  useEffect(() => {
    sessionRef.current = session || null;
  }, [session]);

  // Auth operations

  /**
   * Password login -- calls the BE, which calls Cognito.
   * The BE also enforces SSO-required rules before invoking Cognito.
   */
  const login = useCallback(
    async ({ username, password, remember }: LoginRequest) => {
      const response = await axios.post<Omit<AuthSession, "username" | "clientType">>(
        "auth/login",
        { username, password, remember },
        { baseURL: environment.newApiUrl, withCredentials: true }
      );
      const newSession: StoredSession = {
        username,
        clientType: "app",
        accessToken: response.data.accessToken,
        issuedAt: response.data.issuedAt,
        expiresIn: response.data.expiresIn,
        refreshToken: response.data.refreshToken,
      };
      writeStoredSession(remember ? null : newSession);
      setSession(newSession);
    },
    [setSession]
  );

  const register = useCallback(({ username, password, name }: RegisterRequest) => {
    return Auth.signUp({
      username,
      password,
      attributes: { name, email: username },
    }).then(() => Promise.resolve());
  }, []);

  const refresh = useCallback((): Promise<AuthSession> => {
    if (!sessionRef.current) {
      return Promise.reject(errorMessages.auth.unauthorized);
    }
    const { username, refreshToken, clientType, appClientId } = sessionRef.current;
    const userClientType = clientType ?? "app";
    // Presence of a stored entry is this tab's signal for "remember" mode -- mirrors the
    // backend only re-issuing the cookie when the incoming request was itself cookie-sourced.
    const wasStoredLocally = readStoredSession() != null;
    return axios
      .post<AuthSession>(
        "auth/refresh",
        { username, refreshToken, clientType: userClientType, appClientId },
        { baseURL: environment.newApiUrl, withCredentials: true }
      )
      .then((response) => {
        const newSession: StoredSession = { ...response.data, username, clientType: userClientType };
        if (wasStoredLocally) writeStoredSession(newSession);
        setSession(newSession);
        return response.data;
      })
      .catch((error) => {
        if (wasStoredLocally) writeStoredSession(null);
        setSession(null);
        throw error;
      });
  }, [setSession]);

  const resetPassword = useCallback((request: VerificationCodeRequest) => {
    return Auth.forgotPassword(request.username).then(() => Promise.resolve());
  }, []);

  const verifyResetPassword = useCallback(
    (request: VerifyResetRequest) => {
      return Auth.forgotPasswordSubmit(request.username, request.verificationCode, request.password).then(() => login(request));
    },
    [login]
  );

  const verifySignUp = useCallback(
    (request: VerifyRegisterRequest) => {
      return Auth.confirmSignUp(request.username, request.verificationCode).then(() => login(request));
    },
    [login]
  );

  const resendSignUpCode = useCallback((request: VerificationCodeRequest) => {
    return Auth.resendSignUp(request.username).then(() => Promise.resolve());
  }, []);

  const signOut = useCallback(() => {
    return Promise.all([
      Auth.signOut({ global: true }).catch(() => null),
      axios.post("auth/logout", {}, { baseURL: environment.newApiUrl, withCredentials: true }).catch(() => null),
    ]).then(() => {
      triggerAppEvent(AppEvent.signOut);
      writeStoredSession(null);
      setSession(null);
      queryClient.clear();
      authChannel?.postMessage("signOut");
    });
  }, [setSession, queryClient]);

  // Cross-tab logout: a "remember"-mode session is shared across tabs via the cookie, so a
  // logout in one tab must clear the others too, or a "logged out" tab could keep showing
  // stale authenticated data until its next failed request. Also clears this tab's own
  // sessionStorage entry, if any, so a non-"remember" tab is signed out too when the user
  // explicitly logs out anywhere in the same browser.
  useEffect(() => {
    if (!authChannel) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data === "signOut") {
        writeStoredSession(null);
        setSessionRef.current(null);
        queryClient.clear();
      }
    };

    authChannel.addEventListener("message", onMessage);
    return () => authChannel.removeEventListener("message", onMessage);
  }, [queryClient]);

  const changePassword = useCallback(async (request: ChangePasswordRequest): Promise<void> => {
    const authUser = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(authUser, request.password, request.newPassword);
  }, []);

  const contextValue: AuthContext = {
    session,
    setSession,
    isAuthReady,
    login,
    register,
    refresh,
    verifySignUp,
    resendSignUpCode,
    resetPassword,
    verifyResetPassword,
    signOut,
    changePassword,
  };

  return <Context.Provider value={{ ...contextValue }}>{props.children}</Context.Provider>;
}

function useAuthContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(errorMessages.context.useHookWithinProvider);
  }
  return context;
}

export { AuthContextProvider, useAuthContext };